import React, { useEffect, useState } from "react";

import { useAnalytics } from "@accurx/analytics";
import { NhsOrganisationDto } from "@accurx/api/account";
import { useCurrentWorkspace } from "@accurx/auth";
import { Ds, Feedback } from "@accurx/design";
import { useAccurxWebTitle } from "@accurx/navigation";
import {
    WORKSPACE_REQUIRES_TO_BE_MAPPED,
    useGetSuggestedOrganisationsForWorkspace,
    useMapToAddressMutation,
    useMapToNhsOrganisationMutation,
    useWorkspaceRequiresToBeMappedByUser,
} from "@accurx/workspace-management";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { ROUTES } from "shared/Routes";

import {
    PAGE_PARAM_NAME,
    SEARCH_TERM_PARAM_NAME,
} from "../shared/OrganisationSearch/constants";
import { StyledPageWrapper } from "./MapWorkspacePage.styles";
import {
    UserSelectedOrganisation,
    UserTypedOrganisation,
} from "./MapWorkspacePage.types";
import { MapWorkspaceConfirmModal } from "./components/ConfirmModal/MapWorkspaceConfirmModal";
import { MapWorkspaceAddOrg } from "./components/MapWorkspaceAddOrg";
import { MapWorkspaceInitial } from "./components/MapWorkspaceInitial";
import { MapWorkspaceNoMappingIsNeeded } from "./components/MapWorkspaceNoMappingIsNeeded";
import { MapWorkspaceSelectOrg } from "./components/SelectOrg/MapWorkspaceSelectOrg";
import { STATE_PARAM_NAME } from "./util/constants";

const PAGE_TITLE = "Select an organisation to continue";

export const PAGE_STATES = {
    INITIAL: "0",
    SELECT_ORG: "1",
    ADD_ORG: "2",
};

export const MapWorkspacePage = () => {
    useAccurxWebTitle(PAGE_TITLE);

    const track = useAnalytics();

    const workspace = useCurrentWorkspace();
    const history = useHistory();
    const queryClient = useQueryClient();

    const params = new URLSearchParams(history.location.search);
    const pageStateParam = params.get(STATE_PARAM_NAME);

    const [modalOpen, setModalOpen] = useState(false);

    const [selectedOrganisation, setSelectedOrganisation] =
        useState<UserSelectedOrganisation | null>(null);
    const [typedOrganisation, setTypedOrganisation] =
        useState<UserTypedOrganisation | null>(null);

    const { mutate: mapToNhsOrganisation } = useMapToNhsOrganisationMutation(
        workspace.orgId,
    );

    const { mutate: mapToAddedOrganisation } = useMapToAddressMutation(
        workspace.orgId,
    );

    const onCompleteMappingFlow = () => {
        queryClient.setQueryData(
            [WORKSPACE_REQUIRES_TO_BE_MAPPED, { workspaceId: workspace.orgId }],
            false,
        );

        // We don't want state, search_term or page passed to the Desktop Launch page.
        params.delete(STATE_PARAM_NAME);
        params.delete(PAGE_PARAM_NAME);
        params.delete(SEARCH_TERM_PARAM_NAME);

        history.push({
            pathname: ROUTES.desktopLaunch,
            search: params.toString(),
        });
    };

    const onSubmitSuccess = () => {
        toast(
            <Feedback colour="success" title="Organisation submitted">
                Your response will be reviewed
            </Feedback>,
        );
        onCompleteMappingFlow();
    };

    const onSubmitError = () => {
        toast(
            <Feedback colour="error" title="An error has occurred">
                Next time you log in, you will be taken through this flow again
            </Feedback>,
        );
        // even if request to submit an org fails we want to redirect user to the desktop-launch page
        // not to left them stuck in the flow
        onCompleteMappingFlow();
    };

    const handleSubmit = () => {
        if (selectedOrganisation) {
            mapToNhsOrganisation(
                {
                    organisationId: workspace.orgId,
                    nhsOrganisationId: selectedOrganisation.nhsOrganisationId,
                },
                {
                    onSuccess: onSubmitSuccess,
                    onError: onSubmitError,
                },
            );
        } else if (typedOrganisation) {
            mapToAddedOrganisation(
                {
                    organisationId: workspace.orgId,
                    organisationName: typedOrganisation.organisationName,
                    organisationOdsCode: typedOrganisation.organisationOdsCode,
                    organisationPostcode:
                        typedOrganisation.organisationPostcode,
                },
                {
                    onSuccess: onSubmitSuccess,
                    onError: onSubmitError,
                },
            );
        }
    };

    const onSelectOrganisation = (organisation: NhsOrganisationDto) => {
        setSelectedOrganisation({
            organisationName: organisation.name,
            nhsOrganisationId: parseInt(organisation.id, 10),
        });

        setTypedOrganisation(null);

        setModalOpen(true);
    };

    const addOrgClick = () => {
        setSelectedOrganisation(null);
        setTypedOrganisation(null);

        params.set(STATE_PARAM_NAME, PAGE_STATES.ADD_ORG);

        history.push({
            search: params.toString(),
        });
    };

    const onAddOrganisation = ({
        organisationName,
        organisationPostcode,
        organisationOdsCode,
    }: UserTypedOrganisation) => {
        setTypedOrganisation({
            organisationName,
            organisationPostcode,
            organisationOdsCode,
        });
        setModalOpen(true);
    };

    const openSelectScreen = () => {
        params.set(STATE_PARAM_NAME, PAGE_STATES.SELECT_ORG);

        history.push({
            search: params.toString(),
        });
    };

    const { data: isUserRequiredToMapWorkspace, isFetching } =
        useWorkspaceRequiresToBeMappedByUser({
            workspaceId: workspace.orgId,
            enabled: workspace.settings.isAdminUser,
        });

    // We start requesting the suggested organisations early, so they'll be ready by the time the user reaches the Select screen.
    const {
        data: suggestedOrganisationsData,
        fetchStatus: suggestedOrganisationsStatus,
    } = useGetSuggestedOrganisationsForWorkspace({
        workspaceId: workspace.orgId,
        enabled:
            !!workspace.orgId &&
            !!isUserRequiredToMapWorkspace &&
            workspace.settings.isAdminUser,
    });

    useEffect(() => {
        if (isUserRequiredToMapWorkspace) {
            track("WorkspaceNationalCodeMapping Page View", {});
        }
    }, [isUserRequiredToMapWorkspace, track]);

    if (isFetching) {
        return (
            <StyledPageWrapper alignItems="center" justifyContent="center">
                <Ds.Spinner />
            </StyledPageWrapper>
        );
    }

    if (!isUserRequiredToMapWorkspace || !workspace.settings.isAdminUser) {
        return <MapWorkspaceNoMappingIsNeeded />;
    }

    const displayCurrentScreen = (): JSX.Element => {
        if (pageStateParam === PAGE_STATES.SELECT_ORG) {
            return (
                <MapWorkspaceSelectOrg
                    suggestions={suggestedOrganisationsData}
                    suggestionsFetchStatus={suggestedOrganisationsStatus}
                    onSelectOrganisation={onSelectOrganisation}
                    addOrgClick={addOrgClick}
                />
            );
        }

        if (pageStateParam === PAGE_STATES.ADD_ORG) {
            return (
                <MapWorkspaceAddOrg
                    currentWorkspaceName={workspace.organisationName}
                    onAddOrganisation={onAddOrganisation}
                    onBackClick={openSelectScreen}
                />
            );
        }

        return <MapWorkspaceInitial onButtonClick={openSelectScreen} />;
    };

    const chosenOrganisation =
        selectedOrganisation ?? typedOrganisation ?? null;

    return (
        <>
            {chosenOrganisation && (
                <MapWorkspaceConfirmModal
                    modalOpen={modalOpen}
                    setModalOpen={setModalOpen}
                    organisation={chosenOrganisation}
                    workspaceName={workspace.organisationName}
                    submitCallback={handleSubmit}
                />
            )}
            {displayCurrentScreen()}
        </>
    );
};
